import {
  Wrench, Settings, Palette, Target, Crosshair, RefreshCw, ClipboardCheck,
  Shield, Flag, Users, Award, Star, Heart, CheckCircle, Zap, Clock, MapPin,
  Phone, Mail, Type, Heading, Image as ImageIcon, MousePointerClick, Rows,
  LayoutGrid, Minus, MoveVertical, Megaphone,
} from 'lucide-react';

// Shared lucide icon map for content that stores an icon key.
export const ICON_MAP = {
  wrench: Wrench, settings: Settings, palette: Palette, target: Target,
  crosshair: Crosshair, 'refresh-cw': RefreshCw, 'clipboard-check': ClipboardCheck,
  shield: Shield, flag: Flag, users: Users, award: Award, star: Star,
  heart: Heart, 'check-circle': CheckCircle, zap: Zap, clock: Clock,
  'map-pin': MapPin, phone: Phone, mail: Mail,
};

export const ICON_CHOICES = Object.keys(ICON_MAP);

// Block types that render as full-width, self-styled sections (reuse existing look).
export const SELF_CONTAINED = new Set([
  'hero', 'services', 'gallery_preview', 'pricing', 'image_text', 'values', 'contact',
]);

export const isSelfContained = (block) => SELF_CONTAINED.has(block?.type);

// ---- Section preset → tailwind class maps (no CSS knowledge needed in admin) ----
export const BG_PRESETS = {
  none: '',
  navy: 'bg-slate-900',
  navy_dark: 'bg-slate-950',
  navy_light: 'bg-slate-800',
  accent: 'bg-red-600',
};
export const BG_LABELS = [
  { key: 'none', label: 'Default' },
  { key: 'navy_dark', label: 'Dark Navy' },
  { key: 'navy', label: 'Navy' },
  { key: 'navy_light', label: 'Light Navy' },
  { key: 'accent', label: 'Accent' },
];
export const SPACING_PRESETS = {
  none: '', small: 'py-10', normal: 'py-20', large: 'py-28', xlarge: 'py-36',
};
export const SPACING_LABELS = [
  { key: 'small', label: 'Small' },
  { key: 'normal', label: 'Normal' },
  { key: 'large', label: 'Large' },
  { key: 'xlarge', label: 'Extra Large' },
];
export const WIDTH_PRESETS = {
  narrow: 'max-w-3xl', normal: 'max-w-7xl', wide: 'max-w-[90rem]', full: 'max-w-none',
};
export const WIDTH_LABELS = [
  { key: 'narrow', label: 'Narrow' },
  { key: 'normal', label: 'Normal' },
  { key: 'wide', label: 'Wide' },
  { key: 'full', label: 'Full Width' },
];
export const ALIGN_LABELS = [
  { key: 'left', label: 'Left' },
  { key: 'center', label: 'Center' },
  { key: 'right', label: 'Right' },
];
export const TEXT_SIZE_PRESETS = {
  small: 'text-sm', normal: 'text-base', large: 'text-xl', xlarge: 'text-3xl',
};
export const HEADING_SIZE_PRESETS = {
  small: 'text-2xl', normal: 'text-3xl md:text-4xl', large: 'text-4xl md:text-5xl', xlarge: 'text-5xl md:text-6xl',
};

const nid = () => Math.random().toString(36).slice(2, 12);

// Factory for a brand-new block of a given type with sensible defaults.
export function makeBlock(type) {
  const base = { id: nid(), type, visible: true, settings: {} };
  switch (type) {
    case 'heading':
      return { ...base, content: { text: 'New Heading', align: 'center', size: 'large', color: '#ffffff' } };
    case 'text':
      return { ...base, content: { text: 'Add your text here. Click to edit this block in the panel on the right.', align: 'left', size: 'normal', color: '#e2e8f0' } };
    case 'image':
      return { ...base, content: { url: '', alt: '', align: 'center', width: 100, link: '' } };
    case 'button':
      return { ...base, content: { text: 'Click Here', link: '/#contact', style: 'solid', align: 'center', size: 'normal' } };
    case 'image_text':
      return { ...base, content: { heading: 'Heading', subheading: '', image_url: '', image_caption: '', image_pos: 'left', paragraphs: ['Add your paragraph text here.'] } };
    case 'cards':
      return { ...base, content: { heading: 'Section Heading', items: [
        { id: nid(), icon: 'shield', title: 'Card One', description: 'Describe this card.' },
        { id: nid(), icon: 'award', title: 'Card Two', description: 'Describe this card.' },
      ] }, settings: { columns: 2 } };
    case 'cta':
      return { ...base, content: { heading: 'Ready to get started?', text: 'Reach out today and let’s talk about your project.', button_text: 'Contact Us', button_link: '/#contact' } };
    case 'gallery':
      return { ...base, content: { heading: 'Gallery', items: [] } };
    case 'divider':
      return { ...base, content: {} };
    case 'spacer':
      return { ...base, content: { size: 'normal' } };
    case 'services':
      return { ...base, content: { heading: 'Our Services', subheading: 'What we offer', items: [
        { id: nid(), icon: 'settings', title: 'Service', description: 'Describe this service.', features: ['Feature one', 'Feature two'] },
      ] } };
    default:
      return { ...base, content: {} };
  }
}

// New empty section wrapping a single block of `type`.
export function makeSection(type) {
  return {
    id: nid(),
    visible: true,
    settings: { bg: 'none', spacing: 'normal', width: 'normal', align: 'center', columns: 1 },
    blocks: [makeBlock(type)],
  };
}

// Left-sidebar block library, grouped.
export const BLOCK_LIBRARY = [
  {
    group: 'Basic',
    items: [
      { type: 'heading', label: 'Heading', icon: Heading },
      { type: 'text', label: 'Text', icon: Type },
      { type: 'image', label: 'Image', icon: ImageIcon },
      { type: 'button', label: 'Button', icon: MousePointerClick },
      { type: 'divider', label: 'Divider', icon: Minus },
      { type: 'spacer', label: 'Spacer', icon: MoveVertical },
    ],
  },
  {
    group: 'Layout',
    items: [
      { type: 'image_text', label: 'Image + Text', icon: Rows },
      { type: 'cards', label: 'Cards', icon: LayoutGrid },
    ],
  },
  {
    group: 'Business',
    items: [
      { type: 'services', label: 'Services', icon: Wrench },
      { type: 'cta', label: 'Call To Action', icon: Megaphone },
      { type: 'contact', label: 'Contact', icon: Mail },
    ],
  },
  {
    group: 'Media',
    items: [
      { type: 'gallery', label: 'Gallery', icon: ImageIcon },
    ],
  },
];

// Human labels for section-type blocks (used in editor section headers).
export const BLOCK_LABELS = {
  hero: 'Hero', services: 'Our Services', gallery_preview: 'Gallery',
  pricing: 'Pricing', image_text: 'Image + Text', values: 'Our Values',
  contact: 'Contact', heading: 'Heading', text: 'Text', image: 'Image',
  button: 'Button', cards: 'Cards', cta: 'Call To Action', gallery: 'Gallery',
  divider: 'Divider', spacer: 'Spacer',
};
